import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InputWrapper } from "../../../components/Input/InputWrapper";
import { InputLabel } from "../../../components/Input/InputLabel";
import { SecondaryButton, WarnButton, SuccessButton } from "../../../components/Buttons/SecondaryButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowDown, faTrash, faUndo } from "@fortawesome/free-solid-svg-icons";
import { toBase64 } from "../../../../utils/functions/ImageUtil";

interface OrganizationLogoPickerProps {
  value: string;
  onLogoChange: (file: File) => void;
  onLogoRemove?: () => void;
  onCancelRemove?: () => void;
  disabled: boolean;
  pendingUploadPreview?: string;
  pendingRemoval?: boolean;
}

export function OrganizationLogoPicker({
  value,
  onLogoChange,
  onLogoRemove,
  onCancelRemove,
  disabled,
  pendingUploadPreview,
  pendingRemoval,
}: OrganizationLogoPickerProps) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedLogoBase64, setUploadedLogoBase64] = useState<string>();

  useEffect(() => {
    setUploadedLogoBase64(value);
  }, [value]);

  function handleDropFile(
    e: React.DragEvent<HTMLDivElement | HTMLFormElement>
  ) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    // Dropping a file must respect the disabled state just like the upload
    // button does - otherwise locked branding could still be changed.
    if (disabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      // at least one file has been dropped so do something
      const file = e.dataTransfer.files[0];
      toBase64(file).then(setUploadedLogoBase64);
      if (onLogoChange) onLogoChange(file);
    }
  }

  function handleUploadLogo(event: ChangeEvent<HTMLInputElement>) {
    event.stopPropagation();
    event.preventDefault();

    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      toBase64(file).then((base64) => {
        setUploadedLogoBase64(base64);
        if (onLogoChange) onLogoChange(file);
      });
    }
  }

  function handleDragCatalog(
    e: React.DragEvent<HTMLDivElement | HTMLFormElement>
  ) {
    e.preventDefault();
    e.stopPropagation();
    // No drag affordance (overlay) when the picker is disabled.
    if (disabled) return;
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  }

  function handelUploadClick() {
    inputRef.current?.click();
  }

  function handleRemoveClick() {
    if (onLogoRemove) {
      onLogoRemove();
    }
  }

  function handleCancelRemoveClick() {
    if (onCancelRemove) {
      onCancelRemove();
    }
  }

  // Determine which image to show: pending upload preview > local base64 > server value
  const displaySrc = pendingUploadPreview || uploadedLogoBase64 || value;
  const hasLogo = Boolean(displaySrc) && !pendingRemoval;
  const showRemoveButton = hasLogo && onLogoRemove && !disabled;

  const inputRef = React.useRef<HTMLInputElement>(null);

  return (
    <InputWrapper>
      <InputLabel>{t("logo")}</InputLabel>
      <div className={"relative"}>
        <div
          className={`relative flex-1 border rounded`}
          onDragEnter={handleDragCatalog}
        >
          <input
            ref={inputRef}
            className={"hidden"}
            type="file"
            id="input-file-upload"
            onChange={handleUploadLogo}
            accept="image/png"
          />

          {/* Logo display area */}
          <div
            className={
              "bg-gray-100 border rounded-tl rounded-tr flex justify-center items-center h-32 relative overflow-hidden"
            }
          >
            {pendingRemoval ? (
              // Pending removal state
              <div className="flex flex-col items-center justify-center text-gray-400 p-4">
                <FontAwesomeIcon
                  icon={faTrash}
                  className="text-gray-300 mb-2"
                  size="2x"
                />
                <p className="text-xs text-center text-gray-500 italic">
                  {t("myProfileSub.logoRemovedOnSave")}
                </p>
              </div>
            ) : displaySrc ? (
              // Show logo (either pending upload preview or existing)
              <img
                className={"object-contain h-32 max-h-32 p-4"}
                src={displaySrc}
                alt={"company-logo"}
              />
            ) : (
              // No logo placeholder
              <div className="flex flex-col items-center justify-center text-gray-400 p-4">
                <p className="text-xs text-gray-400 italic">
                  {t("myProfileSub.noLogoUploaded")}
                </p>
              </div>
            )}
          </div>

          {/* Button area */}
          {pendingRemoval ? (
            // Cancel removal button
            <SuccessButton
              onClick={!disabled ? handleCancelRemoveClick : () => {}}
              className={"!rounded-tl-none !rounded-tr-none border-t-0"}
              disabled={disabled}
              width={"full"}
            >
              <FontAwesomeIcon icon={faUndo} className="mr-2" />
              {t("myProfileSub.cancelRemoval")}
            </SuccessButton>
          ) : showRemoveButton ? (
            // Two buttons: Upload and Remove
            <div className="flex">
              <SecondaryButton
                onClick={!disabled ? handelUploadClick : () => {}}
                className={"!rounded-tl-none !rounded-tr-none !rounded-br-none border-t-0 flex-1"}
                disabled={disabled}
              >
                {t("myProfileSub.uploadAFile")}
              </SecondaryButton>
              <WarnButton
                onClick={!disabled ? handleRemoveClick : () => {}}
                className={"!rounded-tl-none !rounded-tr-none !rounded-bl-none border-t-0 border-l-0"}
                disabled={disabled}
                width={"icon"}
              >
                <FontAwesomeIcon icon={faTrash} />
              </WarnButton>
            </div>
          ) : (
            // Single upload button (no logo or remove not available)
            <SecondaryButton
              onClick={!disabled ? handelUploadClick : () => {}}
              className={"!rounded-tl-none !rounded-tr-none border-t-0"}
              disabled={disabled}
              width={"full"}
            >
              {t("myProfileSub.uploadAFile")}
            </SecondaryButton>
          )}

          {/* Drag overlay */}
          {isDragging && (
            <>
              <div
                className={
                  "z-10 absolute w-full h-full top-0 bottom-0 left-0 right-0"
                }
                onDragEnter={handleDragCatalog}
                onDragLeave={handleDragCatalog}
                onDragOver={handleDragCatalog}
                onDrop={handleDropFile}
              ></div>
              <div
                id={"drop-overlay"}
                className={
                  "animate-fadeInFast bg-gray-200 absolute w-full h-full top-0 bottom-0 left-0 right-0 flex justify-center items-center"
                }
              >
                <div className={"space-y-3 flex flex-col h-5 mt-[-60px]"}>
                  <FontAwesomeIcon
                    icon={faFileArrowDown}
                    size={"2x"}
                    className={"mx-auto "}
                  />
                  <p className={"italic font-light text-xs"}>
                    {t("myProfileSub.dropImageHere")}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </InputWrapper>
  );
}
